// LoadingAnimation.js
import React, { useEffect, useState, useRef } from 'react';
import artboardSVG from '../../assets/images/harvestLoader/Artboard 1.svg';

// Matches the viewBox in assets/images/harvestLoader/Artboard 1.svg. Used until the
// file is fetched so the first paint has the right aspect ratio.
const DEFAULT_VIEW_BOX = '0 0 592.33 587.84';

const LoadingAnimation = ({ width = 24, height = 24, backgroundColor = null, color = null }) => {
    const [svgPaths, setSvgPaths] = useState([]);
    const [viewBox, setViewBox] = useState(DEFAULT_VIEW_BOX);
    const [activePathIndex, setActivePathIndex] = useState(0);
    const [isShowing, setIsShowing] = useState(true);

    const activeIndexRef = useRef(0);
    const isShowingRef = useRef(true);
    const timeoutIdRef = useRef(null);

    useEffect(() => {
        // Fetch the SVG content when the component mounts
        fetch(artboardSVG)
            .then((response) => response.text())
            .then((data) => {
                // Parse the SVG content and extract the path elements
                const parser = new DOMParser();
                const svgDoc = parser.parseFromString(data, 'image/svg+xml');

                // Use the file's own viewBox so the artwork fills the requested box
                const sourceViewBox = svgDoc.documentElement.getAttribute('viewBox');
                if (sourceViewBox) setViewBox(sourceViewBox);

                // Extract and parse the CSS styles
                const styleElement = svgDoc.querySelector('style');
                const cssText = styleElement ? styleElement.textContent : '';
                const cssRules = parseCSS(cssText);

                // Extract path elements
                const pathElements = Array.from(svgDoc.querySelectorAll('path'));

                // Store the "d" attribute of each path and apply styles
                const pathsData = pathElements.map((path) => {
                    const d = path.getAttribute('d');

                    // Get class and apply styles
                    const className = path.getAttribute('class');
                    let style = {};
                    if (className && cssRules[`.${className}`]) {
                        style = cssRules[`.${className}`];
                    }

                    // Merge styles, ensuring default values if not specified
                    const fill = style.fill || path.getAttribute('fill') || 'none';
                    const stroke = style.stroke || path.getAttribute('stroke') || 'none';

                    return {
                        d,
                        fill,
                        stroke,
                    };
                });

                setSvgPaths(pathsData);
            });
    }, []);

    useEffect(() => {
        if (svgPaths.length === 0) return;

        function tick() {
            // Update activePathIndex
            if (activeIndexRef.current < svgPaths.length - 1) {
                activeIndexRef.current += 1;
            } else {
                activeIndexRef.current = 0;
                isShowingRef.current = !isShowingRef.current; // Toggle between showing and hiding
            }

            // Update state to trigger re-render
            setActivePathIndex(activeIndexRef.current);
            setIsShowing(isShowingRef.current);

            // Adjust the delay as needed
            let delay = 20;
            if (activeIndexRef.current < 2) delay = 40;
            else if (activeIndexRef.current === svgPaths.length - 1) delay = 250;

            timeoutIdRef.current = setTimeout(tick, delay);
        }

        // Start the animation
        timeoutIdRef.current = setTimeout(tick, 0);

        // Cleanup on unmount
        return () => {
            if (timeoutIdRef.current) {
                clearTimeout(timeoutIdRef.current);
            }
        };
    }, [svgPaths.length]);

    // Function to parse CSS text into an object
    const parseCSS = (cssText) => {
        const rules = {};
        const regex = /([^{]+){([^}]+)}/g;
        let match;
        while ((match = regex.exec(cssText)) !== null) {
            const selector = match[1].trim();
            const declarations = match[2].trim();
            const declarationList = declarations
                .split(';')
                .map((decl) => decl.trim())
                .filter((decl) => decl);
            const styles = {};
            declarationList.forEach((decl) => {
                const [property, value] = decl.split(':').map((str) => str.trim());
                if (property && value) {
                    styles[property] = value;
                }
            });
            rules[selector] = styles;
        }
        return rules;
    };

    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox={viewBox}
            width={width}
            height={height}
            style={{
                backgroundColor: backgroundColor || 'transparent',
                display: 'block', // avoid the inline baseline gap under the svg
            }}
        >
            {svgPaths.map((path, index) => (
                <path
                    key={index}
                    d={path.d}
                    fill={color || path.fill}
                    stroke={color || path.stroke}
                    strokeWidth="2"
                    style={{
                        opacity:
                            isShowing
                                ? index <= activePathIndex
                                    ? 1
                                    : 0
                                : index <= activePathIndex
                                    ? 0
                                    : 1,
                        transition: 'opacity 0.25s ease-in-out',
                    }}
                />
            ))}
        </svg>
    );
};

export default LoadingAnimation;
